import semverGt from "semver/functions/gt"
import { cloneDeep } from "es-toolkit"
import { createPinia, defineStore } from "pinia"
import { ref, computed, type Ref } from "vue"
import { timestamp } from "~/utils/datetime"
import { useCurrentAttackerStore } from "@/stores/currentAttacker"
import { useCurrentDefenderStore } from "@/stores/currentDefender"
import { useAttackContextStore } from "@/stores/attackContext"
import { createSentryPiniaPlugin } from "@sentry/vue"
import { prepareDemoProfiles } from "~/utils/prepareDemoProfiles"
import type { Profile, Unit } from "~/types"

const pinia = createPinia()
pinia.use(createSentryPiniaPlugin())

export const useGlobalStore = defineStore(
  "global",
  () => {
    // State
    const alertExplainScopeGlobal: Ref<boolean> = ref(true)
    const alertNewVersion: Ref<boolean> = ref(false)
    const appVersionLastSeen: Ref<string> = ref("0.0.0")
    const averageType: Ref<string> = ref("median")
    const readOnlyProfiles: Ref<boolean> = ref(false)
    const newUser: Ref<boolean> = ref(true)
    const profiles: Ref<Profile[]> = ref([])
    const profilesLastUpdated: Ref<number | null> = ref(null)
    const profileUiAttacker: Ref<string> = ref("profile")
    const profileUiDefender: Ref<string> = ref("profile")

    // Getters
    const profilesCount = computed(() => {
      return profiles.value.length
    })

    const profilesProcessedAttackersMelee = computed(() => {
      let processedAttackers: Profile[] = []
      if (profiles.value.length) {
        // Filter array to only include eligible melee attackers.
        processedAttackers = profiles.value.filter((profile) => {
          if (profile.computed_roles?.includes("Attacker: Melee")) {
            return true
          }
        })
        // Sort array by `updated` descending.
        processedAttackers.sort((a, b) => {
          const aTime = a.updated ? new Date(a.updated).getTime() : 0
          const bTime = b.updated ? new Date(b.updated).getTime() : 0
          return bTime - aTime
        })
      }
      return processedAttackers
    })

    const profilesProcessedAttackersRanged = computed(() => {
      let processedAttackers: Profile[] = []
      if (profiles.value.length) {
        // Filter array to only include eligible ranged attackers.
        processedAttackers = profiles.value.filter((profile) => {
          if (profile.computed_roles?.includes("Attacker: Ranged")) {
            return true
          }
        })
        // Sort array by `updated` descending.
        processedAttackers.sort((a, b) => {
          const aTime = a.updated ? new Date(a.updated).getTime() : 0
          const bTime = b.updated ? new Date(b.updated).getTime() : 0
          return bTime - aTime
        })
      }
      return processedAttackers
    })

    const profilesProcessedAttackers = computed(() => {
      let processedAttackers: Profile[] = []
      if (profiles.value.length) {
        const attackContextStore = useAttackContextStore()
        if (attackContextStore.attackContext.attackType === "Ranged") {
          processedAttackers = profilesProcessedAttackersRanged.value
        }
        if (attackContextStore.attackContext.attackType === "Melee") {
          processedAttackers = profilesProcessedAttackersMelee.value
        }
      }
      return processedAttackers
    })

    const profilesProcessedDefenders = computed(() => {
      let processedDefenders: Profile[] = []
      if (profiles.value.length) {
        // Filter array to only include eligible defenders.
        processedDefenders = profiles.value.filter((profile) => {
          if (profile.computed_roles?.includes("Defender")) {
            return true
          }
        })
        // Sort array by `updated` descending.
        processedDefenders.sort((a, b) => {
          const aTime = a.updated ? new Date(a.updated).getTime() : 0
          const bTime = b.updated ? new Date(b.updated).getTime() : 0
          return bTime - aTime
        })
      }
      return processedDefenders
    })

    const unitsFromProfiles = computed(() => {
      const units: Unit[] = []
      profiles.value.forEach((profile) => {
        profile.units.forEach((unit) => {
          if (!unit.name) {
            unit.name = profile.name
          }
          units.push(unit)
        })
      })
      return units
    })

    // Actions
    function assignProfileIds() {
      profiles.value.forEach((profile) => {
        profile.id = crypto.randomUUID()
      })
      console.log("Profile IDs assigned.")
      // If IDs are reassigned it's safest/simplest to reset the current attacker / defender.
      const currentAttackerStore = useCurrentAttackerStore()
      const currentDefenderStore = useCurrentDefenderStore()
      currentAttackerStore.setCurrentAttacker(null)
      currentDefenderStore.setCurrentDefender(null)
      console.log("Current attacker & defender reset.")
    }

    async function deleteProfiles(profilesToDelete: Profile[]) {
      const user = useSupabaseUser()
      let databaseSuccess = false

      // Create a copy of the profiles array to avoid modification during iteration
      const profilesToDeleteCopy = [...profilesToDelete]

      for (const profile of profilesToDeleteCopy) {
        // Clear currentAttacker/currentDefender if it matches the profile being deleted.
        const currentAttackerStore = useCurrentAttackerStore()
        const currentDefenderStore = useCurrentDefenderStore()
        if (profile.id === currentAttackerStore.currentAttacker.id) {
          currentAttackerStore.setCurrentAttacker(null)
        }
        if (profile.id === currentDefenderStore.currentDefender.id) {
          currentDefenderStore.setCurrentDefender(null)
        }

        // If the user is logged out, remove the profile from the local store.
        if (!user.value && profile.id) {
          const index = profiles.value.findIndex((p) => p.id === profile.id)
          if (index !== -1) {
            profiles.value.splice(index, 1)
          }
        }
      }

      // Delete the profiles in the db if the user is logged in.
      // The db deletes referenced records (e.g. units) automatically.
      if (user.value) {
        try {
          const supabaseClient = useSupabaseClient()

          const profileIds = profilesToDelete.map((profile) => profile.id)

          const { error: profileError } = await supabaseClient
            .from("profiles")
            .delete()
            .in("id", profileIds)

          if (profileError) {
            console.error("Error deleting profile from database:", profileError)
          } else {
            databaseSuccess = true
          }
        } catch (error) {
          console.error("Error in database operation:", error)
        }
      }

      profilesLastUpdated.value = Date.now()

      if (!user.value || databaseSuccess) {
        // TODO: Fails when built for production so commented out for now.
        // // Display confirmation toast
        // const toast = useToast()
        // const toastContent = `Successfully deleted ${profilesToDelete.length} ${profilesToDelete.length === 1 ? "profile" : "profiles"}.`
        // toast.success(toastContent, {
        //   timeout: 3000,
        // })
      }
    }

    function initAbilitiesSelected(profileRole: "attacker" | "defender") {
      if (profileRole === "attacker") {
        const currentAttackerStore = useCurrentAttackerStore()
        currentAttackerStore.initAbilitiesSelected()
      }

      if (profileRole === "defender") {
        const currentDefenderStore = useCurrentDefenderStore()
        currentDefenderStore.initAbilitiesSelected()
      }
    }

    function initialiseProfileData() {
      console.log("Initialising profile data...")
      setProfiles([])
      const currentAttackerStore = useCurrentAttackerStore()
      const currentDefenderStore = useCurrentDefenderStore()
      currentAttackerStore.setCurrentAttacker(null)
      currentDefenderStore.setCurrentDefender(null)
      profilesLastUpdated.value = Date.now()
      console.log("Profile data initialised.")
    }

    function loadProfiles(
      incomingProfiles: Profile[],
      areDemoProfiles: boolean = false
    ) {
      const profilesData = cloneDeep(incomingProfiles)
      initialiseProfileData()
      if (areDemoProfiles) {
        console.log("Preparing demo profile data...")
        // Generate & use UUIDs etc.
        prepareDemoProfiles(profilesData)
      }
      console.log("Loading profile data...")
      // Write profiles to store.
      setProfiles(profilesData)
      console.log("Profile data loaded.")
      readOnlyProfiles.value = true
      assignProfileIds()
      timestampProfiles()
      readOnlyProfiles.value = false
    }

    function setProfiles(payload: Profile[]) {
      profiles.value = payload
      profilesLastUpdated.value = Date.now()
    }

    function setProfileUi(payload: {
      profileType: "attacker" | "defender"
      profileUiType: string
    }) {
      const { profileType, profileUiType } = payload
      if (profileType === "attacker") {
        profileUiAttacker.value = profileUiType
      }
      if (profileType === "defender") {
        profileUiDefender.value = profileUiType
      }
    }

    function syncAppVersions() {
      const runtimeConfig = useRuntimeConfig()
      const envPackageVersion = runtimeConfig.public.clientVersion
      // Check if version in package.json is > appVersionLastSeen.
      if (semverGt(envPackageVersion, appVersionLastSeen.value)) {
        // Display alert regarding the new version release.
        alertNewVersion.value = true
        // Write current app version from package.json to store.
        appVersionLastSeen.value = envPackageVersion
        console.log(`App version sync complete: ${envPackageVersion}`)
      } else {
        // If version in package.json is <= appVersionLastSeen,
        // make sure that the new version alert is no longer shown.
        alertNewVersion.value = false
      }
    }

    function timestampProfiles() {
      console.log("Updating profile timestamps...")
      const time = timestamp()
      profiles.value.forEach((profile) => {
        profile.created = time
        profile.updated = profile.created
      })
      console.log("Profile timestamps updated.")
    }

    function updateCurrentProfiles() {
      const currentAttackerStore = useCurrentAttackerStore()
      const currentDefenderStore = useCurrentDefenderStore()
      if (typeof currentAttackerStore.currentAttacker?.id !== "undefined") {
        // console.log("Updating current attacker profile data...")
        const currentAttackerId = currentAttackerStore.currentAttacker.id
        const latestAttackerData = profiles.value.find((profile) => {
          if (profile.id === currentAttackerId) {
            return profile
          }
        })
        if (latestAttackerData) {
          currentAttackerStore.setCurrentAttacker(latestAttackerData)
          // console.log("Current attacker profile data updated.")
        } else {
          console.warn(
            `Could not find attacker profile with ID ${currentAttackerId}`
          )
        }
      }
      if (typeof currentDefenderStore.currentDefender?.id !== "undefined") {
        // console.log("Updating current defender profile data...")
        const currentDefenderId = currentDefenderStore.currentDefender.id
        const latestDefenderData = profiles.value.find(
          (profile) => profile.id === currentDefenderId
        )
        if (latestDefenderData) {
          currentDefenderStore.setCurrentDefender(latestDefenderData)
          // console.log("Current defender profile data updated.")
        } else {
          console.warn(
            `Could not find defender profile with ID ${currentDefenderId}`
          )
        }
      }
    }

    function getUpdatedProfileSelectedWeapons(profile: Profile) {
      // Return early with empty arrays if either profile.weapons_selected_* is missing.
      if (
        typeof profile.weapons_selected_melee === "undefined" ||
        typeof profile.weapons_selected_ranged === "undefined"
      ) {
        return { melee: [], ranged: [] }
      }

      // Clone profile.weapons_selected_* so that we can edit an array of selected weapons
      // without affecting the array that we're iterating through
      // (which could potentially alter indices as we're iterating).
      // Using shallow clones since these are just arrays of strings.
      const weaponsSelectedMeleeTempClone = [...profile.weapons_selected_melee]
      const weaponsSelectedRangedTempClone = [
        ...profile.weapons_selected_ranged,
      ]

      // Create an array of IDs of weapons this profile's model_types have.
      let weaponIds: string[] = []
      profile.units.forEach((unit) => {
        for (const modelType of unit.model_types) {
          if (typeof modelType.weapons === "undefined") {
            continue
          }
          weaponIds = [
            ...modelType.weapons.map((weapon) => weapon.id),
            ...weaponIds,
          ]
        }
      })

      // Selected melee weapons.
      profile.weapons_selected_melee.forEach((selectedWeaponId) => {
        if (!weaponIds.includes(selectedWeaponId)) {
          // Profile no longer has this weapon, remove it from weaponsSelectedMeleeTempClone.
          const indexOfWeaponIdToRemove =
            weaponsSelectedMeleeTempClone.indexOf(selectedWeaponId)
          weaponsSelectedMeleeTempClone.splice(indexOfWeaponIdToRemove, 1)
        }
      })

      // Selected ranged weapons.
      profile.weapons_selected_ranged.forEach((selectedWeaponId) => {
        if (!weaponIds.includes(selectedWeaponId)) {
          // Profile no longer has this weapon, remove it from weaponsSelectedRangedTempClone.
          const indexOfWeaponIdToRemove =
            weaponsSelectedRangedTempClone.indexOf(selectedWeaponId)
          weaponsSelectedRangedTempClone.splice(indexOfWeaponIdToRemove, 1)
        }
      })

      return {
        melee: weaponsSelectedMeleeTempClone,
        ranged: weaponsSelectedRangedTempClone,
      }
    }

    // TODO: Swap index for something clearer and better compatible with a database.
    async function writeProfile({
      isClone,
      index,
      profile,
      setAsCurrent,
    }: {
      isClone: boolean
      index: number
      profile: Profile
      setAsCurrent?: "Attacker" | "Defender"
    }) {
      const profileProcessed = cloneDeep(profile)
      profileProcessed.updated = timestamp()

      const user = useSupabaseUser()
      // console.log("user", user) // null || user object

      // Update Existing profile.
      // TODO: Swap index for something clearer and better compatible with a database.
      if (index > -1) {
        // If the user is not logged in, update the profile in the local store.
        if (!user.value) {
          profiles.value.splice(index, 1, profileProcessed)
        }

        // If the user is logged in, update the profile in the database
        if (user.value) {
          try {
            const supabaseClient = useSupabaseClient()

            const { units, ...profileSansUnits } = profileProcessed

            // Update the profile in the db
            const { error: updateProfileError } = await supabaseClient
              .from("profiles")
              .update(profileSansUnits)
              .eq("id", profileProcessed.id)

            if (updateProfileError) {
              console.error(
                "Error updating profile in database:",
                updateProfileError
              )
            }

            // Update the units in the database
            for (const unit of units) {
              // Check if the unit has an ID (existing unit)
              if (unit.id) {
                // First, check if this unit already exists in the database
                // This is necessary because units that have been added to the profile by selecting
                // an existing unit get assigned a new ID before reaching this action.
                // We need to check if they exist in the database before attempting to update them.
                const { data: existingUnit, error: checkUnitError } =
                  await supabaseClient
                    .from("units")
                    .select("id")
                    .eq("id", unit.id)
                    .eq("profile_id", profileProcessed.id)
                    .maybeSingle()

                if (checkUnitError) {
                  console.error(
                    "Error checking if unit exists in database:",
                    checkUnitError
                  )
                }

                if (existingUnit) {
                  // Unit exists, update it
                  const { error: updateUnitError } = await supabaseClient
                    .from("units")
                    .update(unit)
                    .eq("id", unit.id)
                    .eq("profile_id", profileProcessed.id)

                  if (updateUnitError) {
                    console.error(
                      "Error updating unit in database:",
                      updateUnitError
                    )
                  }
                } else {
                  // Unit doesn't exist yet, insert it
                  unit.profile_id = profileProcessed.id
                  const { error: insertUnitError } = await supabaseClient
                    .from("units")
                    .insert(unit)

                  if (insertUnitError) {
                    console.error(
                      "Error inserting unit in database:",
                      insertUnitError
                    )
                  }
                }
              } else {
                // New unit without ID, insert it
                unit.profile_id = profileProcessed.id
                const { error: insertUnitError } = await supabaseClient
                  .from("units")
                  .insert(unit)

                if (insertUnitError) {
                  console.error(
                    "Error inserting unit in database:",
                    insertUnitError
                  )
                }
              }
            }
          } catch (error) {
            console.error("Error in database operation:", error)
          }
        }
      }

      // Add new profile.
      // TODO: Swap index for something clearer and better compatible with a database.
      if (index <= -1) {
        profileProcessed.created = profileProcessed.updated
        profileProcessed.id = crypto.randomUUID()
        // Process weapons.
        // Cloned profiles have an index of -1 just like new profiles.
        // The following weapons processing should only be applied to new, non-cloned profiles.
        if (!isClone) {
          profileProcessed.weapons_selected_melee = []
          profileProcessed.weapons_selected_ranged = []

          profileProcessed.units.forEach((unit) => {
            unit.model_types.forEach((modelType) => {
              modelType.weapons.forEach((weapon) => {
                // Assign ID to weapon and mark it as selected by default
                // (all weapons will be new as it's a new profile).
                weapon.id = crypto.randomUUID()
                const weaponTypeLowerCase = weapon.type.toLowerCase()
                if (weaponTypeLowerCase === "melee") {
                  profileProcessed.weapons_selected_melee.push(weapon.id)
                }
                if (weaponTypeLowerCase === "ranged") {
                  profileProcessed.weapons_selected_ranged.push(weapon.id)
                }
              })
            })
          })
        }

        // User isn't logged in, so just add the profile to the store.
        if (!user.value) {
          profiles.value.push(profileProcessed)
        }

        // User is logged in, so we need to upsert the profile to the db.
        if (user.value) {
          // Have db auto-generate an ID instead.
          delete profileProcessed.id
          // Have db auto-insert timestamps when creating a new profile.
          delete profileProcessed.created
          delete profileProcessed.updated
          const supabaseClient = useSupabaseClient()

          // Upsert profile

          const { units, ...profileSansUnits } = profileProcessed

          const { data: upsertedProfile, error: profileError } =
            await supabaseClient
              .from("profiles")
              .upsert(profileSansUnits)
              .select()
              .single()
          if (profileError) {
            console.error("Error upserting profile:", profileError)
          } else {
            console.log("upsertedProfile", upsertedProfile)
            // Update the local profile with the ID & timestamps from the db.
            profileProcessed.id = upsertedProfile.id
            profileProcessed.created = upsertedProfile.created
            profileProcessed.updated = upsertedProfile.updated
          }

          // Upsert units

          units.forEach((unit) => {
            // Reference the profile ID of the upserted profile.
            unit.profile_id = upsertedProfile.id
          })
          const { data: upsertedUnits, error: unitError } = await supabaseClient
            .from("units")
            .upsert(units)
            .select()
          if (unitError) {
            console.error("Error upserting unit(s):", unitError)
          } else {
            console.log("upsertedUnits", upsertedUnits)
          }
        }
      }

      const currentAttackerStore = useCurrentAttackerStore()
      const currentDefenderStore = useCurrentDefenderStore()
      if (
        setAsCurrent === "Attacker" ||
        profileProcessed?.id === currentAttackerStore.currentAttacker.id
      ) {
        // Update the currently selected attacker profile.
        currentAttackerStore.setCurrentAttacker(profileProcessed)
      }
      if (
        setAsCurrent === "Defender" ||
        profileProcessed?.id === currentDefenderStore.currentDefender.id
      ) {
        // Update the currently selected defender profile.
        currentDefenderStore.setCurrentDefender(profileProcessed)
      }
      if (
        typeof currentAttackerStore.currentAttacker.id !== "undefined" &&
        typeof currentDefenderStore.currentDefender.id !== "undefined" &&
        currentAttackerStore.currentAttacker.id ===
          currentDefenderStore.currentDefender.id
      ) {
        // The same profile is selected as both the current attacker AND defender.
        // Update both.
        currentAttackerStore.setCurrentAttacker(profileProcessed)
        currentDefenderStore.setCurrentDefender(profileProcessed)
      }
      profilesLastUpdated.value = Date.now()

      // TODO: Debugging db query, remove when not needed.
      // If user is logged in, fetch the profile from Supabase to confirm it was written correctly

      // if (user.value && profileProcessed.id) {
      //   try {
      //     const supabaseClient = useSupabaseClient()
      //     const { data: fetchedProfile, error: fetchError } =
      //       await supabaseClient
      //         .from("profiles")
      //         .select("*, units(*)")
      //         .eq("id", profileProcessed.id)
      //         .single()
      //
      //     if (fetchError) {
      //       console.error("Error fetching profile after write:", fetchError)
      //     } else {
      //       console.log(
      //         "Profile successfully written to database:",
      //         fetchedProfile
      //       )
      //     }
      //   } catch (error) {
      //     console.error("Error in database verification operation:", error)
      //   }
      // }
    }

    return {
      // State
      alertExplainScopeGlobal,
      alertNewVersion,
      appVersionLastSeen,
      averageType,
      readOnlyProfiles,
      newUser,
      profiles,
      profilesLastUpdated,
      profileUiAttacker,
      profileUiDefender,
      // Getters
      profilesCount,
      profilesProcessedAttackers,
      profilesProcessedAttackersMelee,
      profilesProcessedAttackersRanged,
      profilesProcessedDefenders,
      unitsFromProfiles,
      // Actions
      assignProfileIds,
      deleteProfiles,
      initAbilitiesSelected,
      initialiseProfileData,
      loadProfiles,
      setProfiles,
      setProfileUi,
      syncAppVersions,
      timestampProfiles,
      updateCurrentProfiles,
      getUpdatedProfileSelectedWeapons,
      writeProfile,
    }
  },
  {
    persist: {
      key: "unitcrunch",
      storage: piniaPluginPersistedstate.localStorage(),
      pick: [
        "alertExplainScopeGlobal",
        "appVersionLastSeen",
        "averageType",
        "newUser",
        "profileUiAttacker",
        "profileUiDefender",
        "profiles",
      ],
    },
  }
)
